import { ref, onMounted, onBeforeUnmount } from 'vue';

// 埋点事件类型
export enum BURIED_EVENT_TYPE {
  CLICK = 'click',
  EXPOSURE = 'exposure',
  CUSTOM = 'custom',
}

//global_buried_click

/*
 * 埋点使用示例：
 *
 * 1. 自动埋点（基于data-buried属性）：
 *    <button data-buried="BUTTON_CLICK" data-buried-params='{"button_type": "primary", "page": "home"}'>
 *      点击按钮
 *    </button>
 *
 * 2. 使用埋点指令：
 *    <button v-buried="{ eventName: 'CUSTOM_CLICK', customData: { action: 'submit' } }"
 *            data-buried-params='{"form_id": "login_form"}'>
 *      提交表单
 *    </button>
 *
 * 3. 手动埋点：
 *    const { track } = useBuriedTracking();
 *    track('MANUAL_EVENT', { user_id: 123, action: 'purchase' });
 *
 * 4. 动态元素埋点：
 *    支持 SPA 中动态渲染的元素，自动监听新添加的埋点元素
 *    如需手动重新观察：const { reobserve } = useBuriedTracking();
 *    reobserve();
 *
 * data-buried-params 支持两种格式：
 * - JSON格式：data-buried-params='{"key": "value", "number": 123}'
 * - URL参数格式：data-buried-params="key=value&number=123"
 */

// 埋点配置接口
export interface BuriedConfig {
  // 是否启用自动埋点
  autoTrack?: boolean;
  // 是否启用点击埋点
  enableClick?: boolean;
  // 是否启用曝光埋点
  enableExposure?: boolean;
  // 曝光阈值（元素可见比例）
  exposureThreshold?: number;
  // 自定义埋点事件
  customEvents?: Record<string, any>;
}

// 埋点数据接口
export interface BuriedData {
  event_type: BURIED_EVENT_TYPE;
  event_name: string;
  element_id?: string;
  element_class?: string;
  element_tag?: string;
  element_text?: string;
  module_name?: string;
  custom_data?: Record<string, any>;
}

// 默认配置
const DEFAULT_CONFIG: BuriedConfig = {
  autoTrack: true,
  enableClick: true,
  enableExposure: true,
  exposureThreshold: 0.5,
  customEvents: {},
};

// 全局埋点实例
let buriedInstance: BuriedTracker | null = null;

// 埋点追踪器类
export class BuriedTracker {
  private config: BuriedConfig;
  private exposureObserver: IntersectionObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private exposedElements: Set<string> = new Set();
  private clickElements: Set<string> = new Set();
  private isInitialized = false;

  constructor(config: BuriedConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // 初始化埋点
  public init(): void {
    if (this.isInitialized || import.meta.server) {
      return;
    }

    this.isInitialized = true;
    this.initExposureTracking();
    this.initClickTracking();
    this.observeNewElements(); // 监听动态添加的元素
  }

  // 初始化曝光埋点
  private initExposureTracking(): void {
    if (!this.config.enableExposure) return;

    this.exposureObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            const elementId = this.getElementId(element);

            if (!this.exposedElements.has(elementId)) {
              this.exposedElements.add(elementId);
              this.trackExposure(element);
            }
          }
        });
      },
      {
        threshold: this.config.exposureThreshold,
        rootMargin: '0px',
      }
    );

    // 观察所有带有埋点属性的元素
    this.observeElements();
  }

  // 初始化点击埋点
  private initClickTracking(): void {
    if (!this.config.enableClick) return;

    document.addEventListener(
      'click',
      event => {
        const target = event.target as HTMLElement;
        if (target) {
          this.trackClick(target);
        }
      },
      true
    );
  }

  // 观察元素
  private observeElements(): void {
    const elements = document.querySelectorAll('[data-buried]');
    elements.forEach(element => {
      this.exposureObserver?.observe(element);
    });
  }

  // 观察新添加的元素（用于动态渲染的元素）
  private observeNewElements(): void {
    // 使用 MutationObserver 监听 DOM 变化
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            // 检查当前元素是否有 data-buried 属性
            if (element.hasAttribute('data-buried')) {
              this.exposureObserver?.observe(element);
            }

            // 检查子元素是否有 data-buried 属性
            const buriedElements = element.querySelectorAll('[data-buried]');
            buriedElements.forEach(buriedElement => {
              this.exposureObserver?.observe(buriedElement);
            });
          }
        });
      });
    });

    // 开始观察整个文档的变化
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // 保存 observer 引用以便后续清理
    this.mutationObserver = observer;
  }

  // 获取元素ID
  private getElementId(element: HTMLElement): string {
    return element.id || element.className || element.tagName || 'unknown';
  }

  // 获取元素文本内容
  private getElementText(element: HTMLElement): string {
    return element.textContent?.trim() || element.title || '';
  }

  // 获取模块上下文
  private getModuleContext(element: HTMLElement): { module_name: string; buried_params: string } {
    let currentElement: HTMLElement | null = element;
    const contactedBuriedNames: string[] = [];
    const buriedParamsMap: Record<string, string> = {};

    while (currentElement) {
      const moduleName = currentElement.dataset.buried;
      const buriedParams = currentElement.dataset.buriedParams;
      if (moduleName) {
        contactedBuriedNames.push(moduleName);
        if (buriedParams) {
          buriedParamsMap[moduleName] = buriedParams;
        }
      }
      currentElement = currentElement.parentElement;
    }
    return {
      module_name: contactedBuriedNames.join('|'),
      buried_params: JSON.stringify(buriedParamsMap),
    };
  }

  // 获取埋点数据
  private getBuriedData(
    element: HTMLElement,
    eventType: BURIED_EVENT_TYPE,
    customData?: Record<string, any>
  ): BuriedData {
    // 获取元素上的埋点参数
    const buriedParams = this.getBuriedParams(element);

    const { module_name, buried_params } = this.getModuleContext(element);

    return {
      event_type: eventType,
      event_name: this.getEventName(element, eventType),
      element_id: element.id || element.getAttribute('name') || undefined,
      element_class: element.className || undefined,
      element_tag: element.tagName.toLowerCase(),
      element_text: this.getElementText(element),
      module_name,
      custom_data: {
        ...buriedParams,
        ...customData,
        ...JSON.parse(buried_params),
      },
    };
  }

  // 获取元素上的埋点参数
  private getBuriedParams(element: HTMLElement): Record<string, any> {
    const paramsAttr = element.getAttribute('data-buried-params');
    if (!paramsAttr) {
      return {};
    }

    try {
      // 尝试解析JSON格式的参数
      return JSON.parse(paramsAttr);
    } catch (error) {
      // 如果JSON解析失败，尝试解析URL参数格式
      try {
        const params = new URLSearchParams(paramsAttr);
        const result: Record<string, any> = {};
        for (const [key, value] of params.entries()) {
          result[key] = value;
        }
        return result;
      } catch (urlError) {
        // 如果都解析失败，返回原始字符串
        console.warn('Failed to parse data-buried-params:', paramsAttr);
        return { raw_params: paramsAttr };
      }
    }
  }

  // 获取事件名称
  private getEventName(element: HTMLElement, eventType: BURIED_EVENT_TYPE): string {
    const buriedAttr = element.getAttribute('data-buried');
    if (buriedAttr) {
      return buriedAttr;
    }

    // 根据元素属性和事件类型生成事件名称
    const prefix = eventType === BURIED_EVENT_TYPE.CLICK ? 'CLICK' : 'EXPOSURE';
    const elementType = element.tagName.toLowerCase();
    const elementId = element.getAttribute('name') || element.id || element.className || 'unknown';

    return `${prefix}_${elementType.toUpperCase()}_${elementId.toString().toUpperCase()}`;
  }

  // 追踪点击事件
  private trackClick(element: HTMLElement): void {
    const elementId = this.getElementId(element);

    // 防抖：避免重复点击
    if (this.clickElements.has(elementId)) {
      return;
    }

    this.clickElements.add(elementId);
    setTimeout(() => {
      this.clickElements.delete(elementId);
    }, 1000);

    const buriedData = this.getBuriedData(element, BURIED_EVENT_TYPE.CLICK);

    // 发送埋点数据
    this.sendBuriedData(buriedData);
  }

  // 追踪曝光事件
  private trackExposure(element: HTMLElement): void {
    const buriedData = this.getBuriedData(element, BURIED_EVENT_TYPE.EXPOSURE);

    // 发送埋点数据
    this.sendBuriedData(buriedData);
  }

  // 发送埋点数据
  private sendBuriedData(data: BuriedData): void {
    const trackName = `global_buried_${data.event_type}`;
    // 控制台输出（开发环境）
    if (process.env.NODE_ENV === 'development') {
      console.log('Buried Event:', data);
      return;
    }
    // 发送到神策数据
    if (window.$sensors?.track) {
      window.$sensors.track(trackName, {
        ...data,
        buried_event_type: data.event_type,
      });
    }

    // 发送到易分析
    if (window._yfx_trackdata) {
      window._yfx_trackdata.push(['event', 'buried', trackName, data]);
    }

    // 发送到Google Analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: trackName,
        ...data,
      });
    }
  }

  // 手动埋点
  public track(eventName: string, customData?: Record<string, any>): void {
    const buriedData: BuriedData = {
      event_type: BURIED_EVENT_TYPE.CUSTOM,
      event_name: eventName,
      custom_data: customData,
    };

    this.sendBuriedData(buriedData);
  }

  // 重新观察所有埋点元素（用于手动触发）
  public reobserveElements(): void {
    if (!this.exposureObserver) return;

    // 先断开所有观察
    this.exposureObserver.disconnect();

    // 重新观察所有现有元素
    this.observeElements();
  }

  // 销毁埋点实例
  public destroy(): void {
    if (this.exposureObserver) {
      this.exposureObserver.disconnect();
      this.exposureObserver = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    this.exposedElements.clear();
    this.clickElements.clear();
    this.isInitialized = false;
  }
}

// 创建埋点实例
export function createBuriedTracker(config?: BuriedConfig): BuriedTracker {
  if (!buriedInstance) {
    buriedInstance = new BuriedTracker(config);
  }
  return buriedInstance;
}

// 获取埋点实例
export function getBuriedTracker(): BuriedTracker | null {
  return buriedInstance;
}

// Vue组合式函数：自动埋点
export function useBuriedTracking(config?: BuriedConfig) {
  const tracker = ref<BuriedTracker | null>(null);

  onMounted(() => {
    tracker.value = createBuriedTracker(config);
    tracker.value.init();
  });

  onBeforeUnmount(() => {
    tracker.value?.destroy();
    tracker.value = null;
  });

  return {
    tracker,
    track: (eventName: string, customData?: Record<string, any>) => {
      tracker.value?.track(eventName, customData);
    },
    reobserve: () => {
      tracker.value?.reobserveElements();
    },
  };
}

// 全局埋点指令
export const buriedDirective = {
  mounted(el: HTMLElement, binding: any) {
    if (import.meta.server) return;

    const { value } = binding;
    const eventName = typeof value === 'string' ? value : value?.eventName || 'CUSTOM_EVENT';
    const customData = typeof value === 'object' ? value.customData : undefined;

    // 获取元素上的埋点参数
    const buriedParams = buriedDirective.getBuriedParams(el);

    el.addEventListener('click', () => {
      const tracker = getBuriedTracker();
      if (tracker) {
        // 合并指令参数和元素参数
        const mergedData = {
          ...buriedParams,
          ...customData,
        };
        tracker.track(eventName, mergedData);
      }
    });
  },

  // 获取元素上的埋点参数（与BuriedTracker中的方法保持一致）
  getBuriedParams(element: HTMLElement): Record<string, any> {
    const paramsAttr = element.getAttribute('data-buried-params');
    if (!paramsAttr) {
      return {};
    }

    try {
      // 尝试解析JSON格式的参数
      return JSON.parse(paramsAttr);
    } catch (error) {
      // 如果JSON解析失败，尝试解析URL参数格式
      try {
        const params = new URLSearchParams(paramsAttr);
        const result: Record<string, any> = {};
        for (const [key, value] of params.entries()) {
          result[key] = value;
        }
        return result;
      } catch (urlError) {
        // 如果都解析失败，返回原始字符串
        console.warn('Failed to parse data-buried-params:', paramsAttr);
        return { raw_params: paramsAttr };
      }
    }
  },
};

// 自动埋点插件
export default defineNuxtPlugin(() => {
  if (import.meta.server) return;

  // 在客户端初始化埋点
  const tracker = createBuriedTracker();
  tracker.init();

  return {
    provide: {
      buriedTracker: tracker,
    },
  };
});
